import { AlertTriangle, CircleAlert, Info, type LucideIcon } from 'lucide-react';
import { IssueSeverity } from '@/api/generated-types';
import { pluralize } from '@/utils/formatters';
import type { FieldError } from '@/stores/validationStore';

export const SEVERITY_CLASSES = {
  warning: 'border-warning bg-warning/10',
  error: 'border-destructive bg-destructive/10',
} as const;

export const SEVERITY_TEXT_COLOR: Record<IssueSeverity, string> = {
  [IssueSeverity.Error]: 'text-destructive',
  [IssueSeverity.Warning]: 'text-warning',
  [IssueSeverity.Info]: 'text-info',
};

/** Every severity, most severe first. Must stay consistent with `SEVERITY_RANK` below. */
export const SEVERITIES = [IssueSeverity.Error, IssueSeverity.Warning, IssueSeverity.Info] as const;

/** Sort order, most severe first. */
export const SEVERITY_RANK: Record<IssueSeverity, number> = {
  [IssueSeverity.Error]: 0,
  [IssueSeverity.Warning]: 1,
  [IssueSeverity.Info]: 2,
};

/**
 * Severity → Lucide icon, matching vNext (Detect / Toolit) banner conventions:
 * error/violation = triangle-alert, warning = circle-alert, info = info.
 */
export const SEVERITY_ICON: Record<IssueSeverity, LucideIcon> = {
  [IssueSeverity.Error]: AlertTriangle,
  [IssueSeverity.Warning]: CircleAlert,
  [IssueSeverity.Info]: Info,
};

/** Singular noun for a severity, for counts like "3 errors" (pluralised by the caller). */
export const SEVERITY_NOUN: Record<IssueSeverity, string> = {
  [IssueSeverity.Error]: 'error',
  [IssueSeverity.Warning]: 'warning',
  [IssueSeverity.Info]: 'note',
};

/** Count phrase for one severity, e.g. "3 errors" / "1 note". */
export function severityCountLabel(severity: IssueSeverity, count: number): string {
  return `${count} ${pluralize(count, SEVERITY_NOUN[severity])}`;
}

export function getErrorClasses(error: string | FieldError | undefined): string {
  if (!error) return '';
  if (typeof error === 'object' && error.severity === IssueSeverity.Warning) {
    return SEVERITY_CLASSES.warning;
  }
  return SEVERITY_CLASSES.error;
}
